import axios from "axios";
import md5 from "js-md5";
import { requestSSE } from "@/utils/sse.js";
import { decryptByDES } from "@/utils/crypto.js";

function getCredentials(params) {
  const credentials = JSON.parse(localStorage.getItem("credentials"));
  if (!credentials) {
    return "";
  }
  const { app_key, secret, token } = credentials;
  const builder = `${app_key}=${secret}&body=${JSON.stringify(
    params
  )}&token=${token}`;
  return `token=${token}&app_key=${app_key}&sign=${md5(builder)}`;  
}

/**
 * 获取用户信息
 * @param {string} code - 用户代码
 * @param {string} state - 状态标识
 * @returns {Promise<{app_key: string, secret: string, token: string}>}
 */
export async function getUserInfo(code, state) {
  const response = await axios({
    method: "POST",
    url: "/api/omw-service-wxproxy/userinfo",
    data: {
      code,
      state,
    },
  });

  // 解密获取 app_key 和 secret
  const decryptKey = `${state}_HGOsign`;
  const decryptedData = decryptByDES(response.data.sign, decryptKey);
  const credentials = JSON.parse(decryptedData);

  return {
    app_key: credentials.app_key,
    secret: credentials.secret,
    token: response.data.token,
  };
}

/**
 * 发送聊天消息（SSE 流式响应）
 * @param {object} params - 消息参数
 * @param {string} params.query - 用户问题
 * @param {string} params.conversation_id - 会话ID
 * @param {object} params.inputs - 输入参数
 * @param {object} callbacks - SSE 回调函数
 * @returns {Function} SSE 连接关闭函数
 */
export function sendChatMessage(params, callbacks) {
  const requestParams = {
    inputs: params.inputs || { storeCode: "288" },
    query: params.query,
    response_mode: "streaming",
    conversation_id: params.conversation_id || "",
    user: params.user || "me",
  };

  // 生成签名
  const credentials = getCredentials(requestParams);

  // 构建请求 URL
  const url = `/api/shiji-bykj/echat/question?method=question&${credentials}`;

  // 发起 SSE 请求
  return requestSSE(url, requestParams, callbacks);
}

/**
 * 获取历史消息
 * @param {string} conversationId - 会话ID
 * @param {string} firstId - 第一条消息ID（用于分页）
 * @returns {Promise<any>}
 */
export async function getHistoricalMessages(
  conversationId,
  firstId
) {
  const credentials = getCredentials({});

  let url = `/api/shiji-bykj/echat/history?${credentials}&method=history&user=me&conversation_id=${conversationId}`;
  if (firstId) {
    url += `&first_id=${firstId}`;
  }  

  const response = await axios({
    method: "GET",
    url,
    data: {},
  });

  return response.data;
}

/**
 * 语音转文字
 * @param {Blob} audioBlob - 录音 Blob 对象（MP3 格式）
 * @param {string} mimeType - 录音的 MIME 类型（固定为 'audio/mp3'）
 * @returns {Promise<{text: string}>}
 */
export async function audioToText(audioBlob, mimeType) {
  // 创建 FormData
  const formData = new FormData();

  // 将 Blob 转换为 File 对象（固定为 mp3 格式）
  const audioFile = new File([audioBlob], 'recording.mp3', {
    type: 'audio/mp3',
  });

  formData.append('file', audioFile);
  formData.append('type', 'audio/mp3');

  console.log("发送语音转文字请求:", {
    format: 'mp3',
    mimeType: 'audio/mp3',
    blobSize: audioBlob.size,
  });

  const credentials = getCredentials(formData);

  const url = `/api/shiji-bykj/echat/audioToText?${credentials}&method=audioToText`;
  // 发送请求
  const response = await axios({
    method: "POST",
    url: url,
    data: formData,
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });

  return response.data;
}
